/**
 * HAND-WRITTEN ON PURPOSE. Do not generate this page with /docs-writer and do not move it
 * into docs/. It is an interactive checklist: every mark comes from a live query, so it
 * has no markdown source.
 *
 * It is styled with the `.doc` classes so it reads like the generated pages beside it
 * (globals.css, ".doc layout" block; the checklist rules are the `.doc-step*` set at the
 * end of that block). No DocsToc: four steps are not sections.
 *
 * The prose facts on this page must stay verified against the code. Check them again
 * whenever one of these changes:
 *   - roles, secret levels, which MCP tools a role is given: packages/shared-types/src/rbac.ts
 *   - the tool catalog: packages/shared-types/src/mcp.ts
 *   - token life, revoke, last-used stamp: apps/api/src/app/pat/pat.service.ts,
 *     packages/inventory-core/src/pat.ts
 *   - the systems an access request can name: packages/shared-types/src/access-requests.ts
 *   - routes, the new-member home, who can assign roles: apps/web/src/App.tsx,
 *     apps/web/src/components/AppShell.tsx
 *   - doc labels: apps/web/src/lib/docs.ts
 * Last verified 2026-10-10.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  MCP_TOOLS,
  NEW_MEMBER_MCP_TOOLS,
  SECRET_LEVEL_LABELS,
  canSeeInventory,
  mcpToolAllowed,
} from '@infras/shared-types';
import { Icon } from '../components/Icon';
import { McpConnectSteps } from '../components/docs/live/McpConnectSteps';
import { McpTokens } from '../components/McpTokens';
import { statusBadgeClass } from '../lib/access';
import { useMe } from '../lib/useMe';

/**
 * Onboarding for every role, and the permanent home of a `new_member`.
 *
 * It is a CHECKLIST against the viewer's real state, so the page says what is LEFT
 * rather than what exists.
 *
 * Every mark is read from data, never asserted: token count from `mcpTokens`, role
 * from the server-verified `me`, open access tasks from `tasks(requester: me, access: pending)`. A checklist
 * that guessed would be worse than prose.
 */
const STATE = gql`
  query GetStartedState {
    mcpTokens {
      id
      revoked
      expiresAt
      lastUsedAt
    }
  }
`;

/** Your own tasks whose access grant is still pending. */
const MY_PENDING = gql`
  query GetStartedPendingAccess($me: String!) {
    tasks(filter: { requester: $me, access: ["pending"], limit: 20, sort: "updated" }) {
      items {
        id
        title
        access {
          status
          resourceName
        }
      }
    }
  }
`;

interface PendingRow {
  id: number;
  title: string;
  access?: { status: string; resourceName: string } | null;
}

interface TokenLite {
  id: string;
  revoked: boolean;
  expiresAt?: string;
  lastUsedAt?: string;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** One checklist row. `done` drives the mark, so it has to come from data. */
function Step({
  n,
  title,
  done,
  status,
  children,
}: {
  n: number;
  title: string;
  done: boolean;
  status?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className={`doc-step${done ? ' doc-step--done' : ''}`}>
      <div className="doc-step-head">
        <span className="doc-step-mark" aria-hidden="true">
          {done ? <Icon name="check" /> : n}
        </span>
        <h2 className="doc-step-title">{title}</h2>
        {status && <span className="doc-step-status">{status}</span>}
      </div>
      {children && <div className="doc-step-body">{children}</div>}
    </section>
  );
}

export function GetStartedPage() {
  const { me } = useMe();
  const { data, error } = useQuery<{ mcpTokens: TokenLite[] }>(STATE, {
    fetchPolicy: 'cache-and-network',
  });

  const isNewMember = me?.role === 'new_member';
  const hasInventory = me ? canSeeInventory(me.role) : false;
  const roleLabel = me?.role.replace('_', ' ');

  // Until the query answers, the token steps have no state to report. Saying
  // "no token" while loading, or after a failed load, would be a guess.
  const unknown = data ? null : error ? 'could not check' : 'checking…';

  // "Live" = usable right now. Counting raw rows would tick step 1 for someone
  // whose only token expired last month or was revoked.
  const now = Date.now();
  const liveTokens = (data?.mcpTokens ?? []).filter(
    (t) => !t.revoked && (!t.expiresAt || new Date(t.expiresAt).getTime() > now),
  );
  const usedTokens = liveTokens.filter((t) => t.lastUsedAt);
  const pending = useQuery<{ tasks: { items: PendingRow[] } }>(MY_PENDING, {
    variables: { me: me?.email.toLowerCase() ?? '' },
    skip: !me,
  });
  const openRequests = pending.data?.tasks.items ?? [];

  // The same rule the MCP server applies when it lists tools for a role.
  const toolTotal = MCP_TOOLS.length;
  const toolCount = me ? MCP_TOOLS.filter((t) => mcpToolAllowed(me.role, t.name)).length : 0;

  return (
    <div className="page doc">
      <p className="doc-eyebrow">Onboarding · Checklist</p>
      <h1 className="doc-title">Get started</h1>
      <p className="doc-lede">
        This dashboard is the OptiSigns infrastructure inventory. It covers repos, clusters, databases, secret
        names, scheduler jobs, deploy catalogs, access tasks and alerts. It is built for{' '}
        <strong>AI agents first</strong>. Your agent reads it through a hosted MCP server, and the web pages
        are for manual research. The four steps below show what you have done and what is left.
      </p>
      {roleLabel && <p className="doc-meta">Your role: {roleLabel}</p>}

      <Step
        n={1}
        title="Create a personal access token"
        done={liveTokens.length > 0}
        status={unknown ?? (liveTokens.length > 0 ? plural(liveTokens.length, 'live token') : 'no live token')}
      >
        <McpTokens />
      </Step>

      <Step
        n={2}
        title="Connect your AI agent"
        // A token that has never been used means the agent was never actually
        // wired up. That's the honest signal, and we already store it: the MCP
        // server stamps `last_used_at` each time it accepts the token.
        done={usedTokens.length > 0}
        status={unknown ?? (usedTokens.length > 0 ? 'token used' : 'no token used yet')}
      >
        <McpConnectSteps tokenPanelInline />
      </Step>

      <Step
        n={3}
        title="Get a role assigned"
        done={hasInventory}
        status={
          !me
            ? 'checking…'
            : hasInventory
              ? `role: ${roleLabel}`
              : openRequests.length > 0
                ? `no role yet · ${plural(openRequests.length, 'access task')} pending`
                : 'no role yet'
        }
      >
        {hasInventory ? (
          <p className="doc-p">
            Your role opens the inventory pages and{' '}
            {toolCount === toolTotal ? `all ${toolTotal}` : `${toolCount} of the ${toolTotal}`} MCP tools. It
            also sets which secret names you see, by level:{' '}
            {me?.allowedLevels?.length ? (
              me.allowedLevels.map((l) => (
                <span key={l} className="tag-badge" style={{ marginRight: 4 }}>
                  {SECRET_LEVEL_LABELS[l] ?? l} · {l}
                </span>
              ))
            ) : (
              <span className="muted">none</span>
            )}
          </p>
        ) : (
          <>
            <p className="doc-p">
              As a new member you can read the docs, create a token, ask for access and call these MCP
              tools:{' '}
              {NEW_MEMBER_MCP_TOOLS.map((t, i) => (
                <span key={t}>
                  {i > 0 && ', '}
                  <code>{t}</code>
                </span>
              ))}
              . The inventory pages stay hidden until a <code>root</code> user assigns your role on
              Administration → Users &amp; roles.
            </p>
            <p className="doc-p">
              To ask for a role, open an access task and say which role you need. The form lists company
              systems and has no entry for a dashboard role, so pick <strong>Other</strong> under{' '}
              <strong>Which system?</strong>
            </p>
            <p>
              <Link to="/tasks/new?template=access" className="btn btn-primary">
                Request access
              </Link>
            </p>
          </>
        )}

        {openRequests.length > 0 && (
          <ul className="waiting-list">
            {openRequests.map((r) => (
              <li className="waiting-line" key={r.id}>
                <Link to={`/tasks/${r.id}`}>#{r.id}</Link>
                <span className="waiting-line__what">{r.access?.resourceName ?? r.title}</span>
                <span className={statusBadgeClass(r.access?.status ?? 'pending')}>{r.access?.status ?? 'pending'}</span>
              </li>
            ))}
          </ul>
        )}
      </Step>

      {/* No mark: knowing where things are isn't a task you complete. */}
      <Step n={4} title="Know where to look" done={false}>
        <p className="doc-p">
          <Link to="/docs/mcp">MCP tools</Link> lists every tool and which roles can call it.{' '}
          <Link to="/docs/request-access">Requesting access</Link> explains how to ask for access to a company
          system.
          {hasInventory && (
            <>
              {' '}
              For manual research, open <Link to="/overview">Overview</Link>, <Link to="/repos">Repos</Link>,{' '}
              <Link to="/clusters">Clusters</Link>, <Link to="/secrets">Secrets</Link> or the{' '}
              <Link to="/catalog">Deploy catalog</Link>.
            </>
          )}
        </p>
        {isNewMember && (
          <p className="doc-p">This page is your home until a role is assigned.</p>
        )}
      </Step>
    </div>
  );
}
